import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpContext,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface HttpOptions {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  context?: HttpContext;
  observe?: 'body' | 'events' | 'response';
  params?:
    | HttpParams
    | {
        [param: string]:
          | string
          | number
          | boolean
          | ReadonlyArray<string | number | boolean>;
      };
  reportProgress?: boolean;
  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  withCredentials?: boolean;
  body?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  /**
   * Performs a GET request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param options - Optional HTTP options
   */
  get<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.get<T>(this.buildUrl(endpoint), opts as any) as Observable<T>;
  }

  /**
   * Performs a POST request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param body - The request body
   * @param options - Optional HTTP options
   */
  post<T>(endpoint: string, body?: any, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.post<T>(this.buildUrl(endpoint), body, opts as any) as Observable<T>;
  }

  /**
   * Performs a PUT request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param body - The request body
   * @param options - Optional HTTP options
   */
  put<T>(endpoint: string, body?: any, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.put<T>(this.buildUrl(endpoint), body, opts as any) as Observable<T>;
  }

  /**
   * Performs a PATCH request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param body - The request body
   * @param options - Optional HTTP options
   */
  patch<T>(endpoint: string, body?: any, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.patch<T>(this.buildUrl(endpoint), body, opts as any) as Observable<T>;
  }

  /**
   * Performs a DELETE request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param options - Optional HTTP options
   */
  delete<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.delete<T>(this.buildUrl(endpoint), opts as any) as Observable<T>;
  }

  /**
   * Performs a HEAD request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param options - Optional HTTP options
   */
  head<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.head<T>(this.buildUrl(endpoint), opts as any) as Observable<T>;
  }

  /**
   * Performs an OPTIONS request
   * @param endpoint - The endpoint path (relative to baseUrl)
   * @param options - Optional HTTP options
   */
  options<T>(endpoint: string, options?: HttpOptions): Observable<T> {
    const opts = { ...options, observe: 'body' as const };
    return this.http.options<T>(this.buildUrl(endpoint), opts as any) as Observable<T>;
  }

  /**
   * Builds the full URL from baseUrl and endpoint
   * @param endpoint - The endpoint path
   */
  private buildUrl(endpoint: string): string {
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${this.baseUrl}${path}`;
  }

  /**
   * Gets the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }

  /**
   * Sets a new base URL
   * @param baseUrl - The new base URL
   */
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }
}
